import { ChildProps } from '@apollo/client/react/hoc';
import { gql } from '../gql';
import React from 'react';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import { connect } from '@reverbdotcom/commons/src/connect';
import I18n from 'i18n-js';
import {
  ListingsCollectionFragment,
  Core_Homepage_RecentlyViewedSimilarListingsSearchQuery,
  Core_Homepage_RecentlyViewedSimilarListingsSearchQueryVariables,
} from '../gql/graphql';
import ListingsCollection from '@reverbdotcom/commons/src/components/listings_collection';

interface IExternalProps {
  recentlyViewedListings: ListingsCollectionFragment[];
  largeTiles?: boolean;
  maxCount?: number;
  displayAsGrid?: boolean;
  jumplinkSlug?: string;
  displayAddToCart?: boolean;
  showFlags?: boolean;
  showShippingDisplay?: boolean;
  cmsComponentId?: string;
}

const COMPONENT_NAME = 'RecentlyViewedAndMore';
const MIN_LISTINGS = 6;

export type IProps = ChildProps<IExternalProps, Core_Homepage_RecentlyViewedSimilarListingsSearchQuery>;

export function RecentlyViewedAndMoreListings(props: IProps) {
  const {
    recentlyViewedListings,
    displayAsGrid,
    largeTiles,
    maxCount,
    jumplinkSlug,
    displayAddToCart,
    showFlags,
    showShippingDisplay,
    cmsComponentId,
  } = props;


  const loading = props.data?.loading;
  const totalListings = recentlyViewedListings.concat(
    props.data?.similarListings0?.listings || [],
    props.data?.similarListings1?.listings || [],
  );

  if (totalListings.length < MIN_LISTINGS) { return null; }

  const title = I18n.t('discovery.favorites.feed.tabs.recentlyViewed');

  return (
    <ListingsCollection
      componentName={COMPONENT_NAME}
      cmsComponentId={cmsComponentId}
      displayAsGrid={displayAsGrid}
      largeTiles={largeTiles}
      listings={totalListings.slice(0, MIN_LISTINGS)}
      loading={loading}
      maxCount={maxCount}
      title={title}
      jumplinkSlug={jumplinkSlug}
      displayAddToCart={displayAddToCart}
      showFlags={showFlags}
      showShippingDisplay={showShippingDisplay}
    />
  );
}

const withListingsSearchQuery = withGraphql<
  IProps,
  Core_Homepage_RecentlyViewedSimilarListingsSearchQuery,
  Core_Homepage_RecentlyViewedSimilarListingsSearchQueryVariables
>(
  gql(`query Core_Homepage_RecentlyViewedSimilarListingsSearch(
    $limit: Int
    $recentSearchId0: String
    $recentSearchId1: String
    $skipQuery1: Boolean!
  ) {
    similarListings0: similarListingsSearch(input: {
      limit: $limit
      listingId: $recentSearchId0
      includeForLiveListings: true
    }) {
      listings {
        _id
        ... ListingsCollection
        ... mParticleListingFields
      }
    }
    similarListings1: similarListingsSearch(input: {
      limit: $limit
      listingId: $recentSearchId1
      includeForLiveListings: true
    }) @skip(if: $skipQuery1) {
      listings {
        _id
        ... ListingsCollection
        ... mParticleListingFields
      }
    }
  }
  `),
  {
    options: (props: IProps) => ({
      skip: !props.recentlyViewedListings.length,
      ssr: false,
      variables: {
        limit: props.recentlyViewedListings.length === 1 ? 5 : 2, // we want a total of 6 listings
        recentSearchId0: props.recentlyViewedListings[0]?.id,
        recentSearchId1: props.recentlyViewedListings[1]?.id,
        skipQuery1: !props.recentlyViewedListings[1],
      },
    }),
  },
);

export default connect<IExternalProps>([
  withListingsSearchQuery,
])(RecentlyViewedAndMoreListings);
